import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Card } from "@/components/ui/card";
import { Newspaper, Users, Award, Trophy } from "lucide-react";

export default function AdminOverviewPage() {
  const { current } = useChampionship() || {};
  const [stats, setStats] = useState({ teams: 0, players: 0, news: 0, sponsors: 0, captains: 0, pending_captains: 0, pending_players: 0 });

  const load = useCallback(async () => {
    if (!current?.id) return;
    try {
      const [t, n, s, c, p] = await Promise.all([
        api.get(`/championships/${current.id}/teams`),
        api.get(`/championships/${current.id}/news`),
        api.get(`/championships/${current.id}/sponsors`),
        api.get(`/admin/captains`),
        api.get(`/admin/championships/${current.id}/players`),
      ]);
      const teams = t.data || [];
      const captains = c.data || [];
      const playersList = p.data || [];
      setStats({
        teams: teams.length,
        players: teams.reduce((a, x) => a + x.players.length, 0),
        news: (n.data || []).length,
        sponsors: (s.data || []).length,
        captains: captains.length,
        pending_captains: captains.filter((u) => !u.approved).length,
        pending_players: playersList.filter((pl) => !pl.approved).length,
      });
    } catch {
      /* stats are non-critical; ignore */
    }
  }, [current?.id]);

  useEffect(() => { load(); }, [load]);

  const cards = [
    { label: "Countries", value: stats.teams, icon: Trophy },
    { label: "Players", value: stats.players, icon: Users },
    { label: "News articles", value: stats.news, icon: Newspaper },
    { label: "Sponsors", value: stats.sponsors, icon: Award },
    { label: "Captains", value: stats.captains, icon: Users },
  ];

  return (
    <div className="p-8 lg:p-10" data-testid="admin-overview">
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Overview</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tighter mt-2">
        WOC {current?.year} — {current?.location}
      </h1>
      <p className="text-muted-foreground mt-1">{current?.tagline}</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 border border-border bg-white rounded-none">
            <c.icon className="h-5 w-5 text-primary mb-3" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{c.label}</div>
            <div className="font-display text-3xl font-extrabold mt-1 tracking-tight">{c.value}</div>
          </Card>
        ))}
      </div>

      {(stats.pending_captains > 0 || stats.pending_players > 0) && (
        <div className="mt-10 border-l-4 border-amber-500 bg-amber-50 p-5" data-testid="pending-approvals-banner">
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700 mb-2">Action required</div>
          <div className="font-display text-lg font-bold mb-3">You have pending approvals.</div>
          <div className="flex flex-wrap gap-3 text-sm">
            {stats.pending_captains > 0 && (
              <a href="/admin/captains" className="text-primary underline">
                {stats.pending_captains} captain{stats.pending_captains > 1 ? "s" : ""} awaiting approval →
              </a>
            )}
            {stats.pending_players > 0 && (
              <a href="/admin/players" className="text-primary underline">
                {stats.pending_players} player{stats.pending_players > 1 ? "s" : ""} awaiting approval →
              </a>
            )}
          </div>
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-none">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Current edition</div>
          <div className="font-display text-2xl font-bold">{current?.year} · {current?.location}</div>
          <div className="text-sm text-muted-foreground mt-1">{current?.start_date} → {current?.end_date}</div>
        </Card>
        <Card className="p-6 rounded-none">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Quick links</div>
          <ul className="space-y-2 text-sm">
            <li><a className="text-primary underline" href="/admin/news">Add news article →</a></li>
            <li><a className="text-primary underline" href="/admin/captains">Approve captains →</a></li>
            <li><a className="text-primary underline" href="/admin/championships">Manage editions →</a></li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
